import { ReactElement, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { availableObjectTypesOptions } from "../../lib/queries";
import { ReactComponent as SearchIcon } from "../../assets/icons/search 1.svg";
import { trackSearchQuery } from "../../analytics";

export default function SearchSection(): ReactElement {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [error] = useState("");
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [selectedObjectTypes, setSelectedObjectTypes] = useState<string[]>([]);

  const { data: availableTypes = [] } = useQuery(availableObjectTypesOptions());

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim() || selectedObjectTypes.length > 0) {
      const trimmedQuery = query.trim();
      const searchType = /^[a-fA-F0-9]{64}$/.test(trimmedQuery)
        ? "sha"
        : "filename";
      trackSearchQuery(trimmedQuery, searchType);
      navigate("/allscans", {
        state: {
          searchQuery: trimmedQuery,
          selectedObjectTypes,
        },
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTypeDropdown(false);
      }
    };

    if (showTypeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTypeDropdown]);

  return (
    <div className="search-container">
      <div className="search-inner">
        <SearchIcon className="search-img" />
        <h2 className="upload-drag-drop-title">Search Community Database</h2>
        <p className="search-text">
          Look up files by filename or SHA256 hash to see scan results and
          reports.
        </p>

        <form className="searchbar" onSubmit={handleSearch}>
          <img src="/search-grey.svg" alt="search" className="search-img-bar" />

          <input
            className="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by filename or SHA256 hash..."
            aria-label="Search by filename or SHA256 hash"
          />

          {selectedObjectTypes.length > 0 && (
            <div className="active-filters-container">
              {selectedObjectTypes.map((type) => (
                <div key={type} className="active-filter-badge">
                  {type}
                  <button
                    type="button"
                    className="badge-clear-btn"
                    onClick={() =>
                      setSelectedObjectTypes(
                        selectedObjectTypes.filter((t) => t !== type),
                      )
                    }
                    title="Remove filter"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
            <button
              type="button"
              className="search-filter-btn"
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              aria-label="Filters"
            >
              <img src="/filter.svg" alt="filter" />
            </button>
            {showTypeDropdown && (
              <div className="filter-dropdown-menu">
                {availableTypes.length > 0 ? (
                  availableTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`filter-option ${
                        selectedObjectTypes.includes(type) ? "active" : ""
                      }`}
                      onClick={() => {
                        let updatedTypes: string[];
                        if (selectedObjectTypes.includes(type)) {
                          updatedTypes = selectedObjectTypes.filter(
                            (t) => t !== type,
                          );
                        } else {
                          updatedTypes = [...selectedObjectTypes, type];
                        }
                        setSelectedObjectTypes(updatedTypes);
                        navigate("/allscans", {
                          state: {
                            searchQuery: query,
                            selectedObjectTypes: updatedTypes,
                          },
                        });
                      }}
                    >
                      {type}
                    </button>
                  ))
                ) : (
                  <div className="filter-no-types">No types available</div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
      {error && <div className="search-error">{error}</div>}
    </div>
  );
}
